import isEqual from 'lodash/isEqual';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectDoesUserHaveRbacPermission } from 'core/userContext/selectors';
import {
  DEFAULT_SEARCH_COMBINATOR,
  DEFAULT_SEARCH_DATATYPE,
} from 'modules/journey/components/FilterInputsList/FilterInputsList.constants';
import { WORKFLOW_TRIGGERS } from 'modules/workflows';
import { NodeEditor } from 'modules/workflows/components/NodeEditor';
import { SimpleOverlayContainer } from 'modules/workflows/components/NodeEditor/ResizableOverlay';
import { TRIGGER_METADATA } from 'modules/workflows/constants';
import {
  getIsNodeEditorReadOnly,
  getIsStartTileRefreshEnabled,
  updateNodeData,
  updateWorkflow,
} from 'modules/workflows/ducks';
import { useDisplayCloneErrors } from 'modules/workflows/hooks/useDisplayCloneErrors';
// import { StartNodeData } from 'modules/workflows/types';
import { startUtils } from 'modules/workflows/utils';
import { PROJECT_PERMISSIONS } from 'utils/permissions';

import { CAMPAIGN_LABEL } from 'utils/fieldTypes';
import { FormSectionContent } from '../../EditorBody.styled';
// import { EditorBaseProps } from '../../types';
import { validateEntranceLimit, validateSimultaneousLimit } from '../../utils';
import { StartNodeEditor as StartNodeEditorRefresh } from '../StartNodeEditor';
import { useClonedQueryErrors } from '../StartNodeEditor/StartNodeEditor.hooks';
import {
  getStartNodeEditorNodeDataToBeCompared,
  parseWorkflowForSaving,
} from '../StartNodeEditor/StartNodeEditor.utils';
import { NodeErrorBanner } from '../NodeErrorBanner';
import EntranceLimitForm, { startEditorText } from './components/EntranceLimitForm';
import { FiltersInput } from './components/FiltersInput';
import ResourceForm from './components/ResourceForm';
import { TriggerTypeInput } from './components/TriggerTypeInput';
import {
  getQueriesStub,
  getUpdatedSearchCombo,
  parseNodeDataForSaving,
  parseNodeDataForUpdatedTrigger,
} from './utils';

// export type StartNodeEditorProps = EditorBaseProps<StartNodeData>;

export const START_NODE_EDITOR_TEST_ID = 'start-node-editor';

/**
 * StartNodeEditorCurrent
 * @param {StartNodeEditorProps} props
 */
const StartNodeEditorCurrent = ({
  nodeData: initialNodeData = {},
  workflowData: initialWorkflowData = {},
  text = startEditorText,
  betaNodes = [],
  nodeId,
  clonedFromNodeData,
  ...rest
}) => {
  const dispatch = useDispatch();
  const canDraftMessaging = useSelector((state) =>
    selectDoesUserHaveRbacPermission(state)(PROJECT_PERMISSIONS.DraftMessaging),
  );
  const isReadOnlyTileEditorsEnabled = true;
  const isReadOnly = useSelector(getIsNodeEditorReadOnly);
  const isTileEditorReadOnly = isReadOnlyTileEditorsEnabled && isReadOnly;

  const [nodeData, setNodeData] = useState(initialNodeData || {});
  const [workflowData, setWorkflowData] = useState(initialWorkflowData || {});
  const [areValidationsShown, setAreValidationsShown] = useState(false);
  const [errors, setErrors] = useState({ lifetimeLimit: null, simultaneousLimit: null });

  const triggerResource = TRIGGER_METADATA[nodeData?.nodeClass];
  const { TriggerForm, hasFilter, dataType = DEFAULT_SEARCH_DATATYPE } = triggerResource || {};
  const isTriggeredByWorkflow = nodeData.nodeClass === WORKFLOW_TRIGGERS.TRIGGERED_BY_WORKFLOW;
  const hasError = (data) => {
    if (!startUtils.isValid(data, workflowData)) return true;
    return Object.values(errors).some((v) => v != null);
  };

  const updateInternalNodeData = (updates = {}) => setNodeData({ ...nodeData, ...updates });

  const updateInternalWorkflowData = (updates = {}) =>
    setWorkflowData({ ...workflowData, ...updates });

  const updateSearchCombo = (updates = {}) =>
    updateInternalNodeData({
      searchCombo: getUpdatedSearchCombo(updates, { nodeData, dataType }),
    });

  const handleUpdate = (requiresValidation = true) => {
    const updatedNodeData = parseNodeDataForSaving(nodeData);

    if (requiresValidation && hasError(updatedNodeData)) {
      setAreValidationsShown(true);
    } else {
      dispatch(updateWorkflow(parseWorkflowForSaving(workflowData, { nodeData: updatedNodeData })));
      dispatch(updateNodeData(updatedNodeData, nodeId));
    }
  };

  const handleTriggerTypeSelect = (selectedOption) => {
    updateInternalNodeData(parseNodeDataForUpdatedTrigger(selectedOption, initialNodeData));
  };

  const getHasUnsavedChanges = () => {
    const cleanedInitialNodeData = getStartNodeEditorNodeDataToBeCompared({
      ...initialNodeData,
      ...initialWorkflowData,
    });
    const updatedNodeData = getStartNodeEditorNodeDataToBeCompared({
      ...nodeData,
      ...workflowData,
    });
    return !isEqual(cleanedInitialNodeData, updatedNodeData);
  };

  const resourceField = startUtils.getResourceField({
    nodeClass: initialNodeData.nodeClass,
  });

  const searchQueriesErrors = useClonedQueryErrors({
    clonedFromSearchCombo: clonedFromNodeData?.searchCombo,
    initialSearchCombo: initialNodeData?.searchCombo,
    searchQueries: nodeData?.searchCombo?.searchQueries,
    updateSearchCombo,
    dataType,
  });

  const { isCloneWarningBannerShown, cloneErrors, cloneDisplayValues } = useDisplayCloneErrors({
    clonedFromNodeData,
    fieldsToValidate: {
      [resourceField]: {
        isValid: nodeData[resourceField],
        displayValue: clonedFromNodeData?.resource?.name,
      },
      searchCombo: {
        isValid: Object.keys(searchQueriesErrors).length === 0,
      },
    },
    nodeClass: nodeData?.nodeClass,
  });

  useEffect(() => {
    const { workflowLifetimeLimit: ll, workflowSimultaneousLimit: sl } = workflowData;
    const llError = validateEntranceLimit(ll);
    const slError = validateSimultaneousLimit(sl, ll, text);
    if (errors.lifetimeLimit !== llError || errors.simultaneousLimit !== slError) {
      setErrors({ lifetimeLimit: llError, simultaneousLimit: slError });
    }
  }, [workflowData.workflowLifetimeLimit, workflowData.workflowSimultaneousLimit]);

  useEffect(() => {
    if (areValidationsShown && !hasError(nodeData)) {
      // remove validation errors once errors are fixed
      setAreValidationsShown(false);
    }
  }, [nodeData]);

  // selected trigger type doesn't exist (not in beta)
  if (!triggerResource) {
    return null;
  }

  return (
    <NodeEditor
      {...rest}
      dataTest={START_NODE_EDITOR_TEST_ID}
      onUpdate={handleUpdate}
      getHasUnsavedChanges={getHasUnsavedChanges}
      usesCustomOverlay={isTriggeredByWorkflow}
      isCloneWarningBannerShown={isCloneWarningBannerShown}
    >
      {areValidationsShown && <NodeErrorBanner />}
      <FormSectionContent>
        <TriggerTypeInput
          value={nodeData.nodeClass}
          onChange={handleTriggerTypeSelect}
          betaNodes={betaNodes}
          currentWorkflow={workflowData}
          showOverlay={isReadOnly && isTriggeredByWorkflow && !isReadOnlyTileEditorsEnabled}
          isTriggeredByWorkflow={isTriggeredByWorkflow}
          isReadOnly={isTileEditorReadOnly}
        />
        <SimpleOverlayContainer
          showOverlay={isReadOnly && isTriggeredByWorkflow && !isReadOnlyTileEditorsEnabled}
          showSpinner={false}
        >
          {triggerResource.resourceType && (
            <ResourceForm
              areValidationsShown={areValidationsShown || cloneErrors[resourceField]}
              displayValue={cloneDisplayValues[resourceField]}
              nodeData={nodeData}
              trigger={nodeData.nodeClass}
              onChange={({ value, resourceName, fieldName }) =>
                // only set node data if there is a resource
                // not when triggered by an initial render
                resourceName &&
                updateInternalNodeData({
                  [fieldName]: value,
                  resource: { name: resourceName },
                  // reset searchQueries if changing custom event
                  ...(fieldName === 'eventName'
                    ? {
                        searchCombo: {
                          searchQueries: getQueriesStub(),
                          combinator: DEFAULT_SEARCH_COMBINATOR,
                        },
                      }
                    : {}),
                })
              }
              isReadOnly={isTileEditorReadOnly}
            />
          )}
          {TriggerForm && (
            <TriggerForm
              onChange={updateInternalNodeData}
              nodeData={nodeData}
              isReadOnly={isTileEditorReadOnly}
            />
          )}
          {hasFilter && (
            <FiltersInput
              areValidationsShown={areValidationsShown}
              combinator={nodeData.searchCombo?.combinator || DEFAULT_SEARCH_COMBINATOR}
              dataType={dataType}
              initialQueries={initialNodeData.searchCombo?.searchQueries}
              onCombinatorUpdate={(combinator) => updateSearchCombo({ combinator })}
              onSearchQueriesUpdate={(searchQueries) => updateSearchCombo({ searchQueries })}
              queries={nodeData.searchCombo?.searchQueries}
              queriesErrors={searchQueriesErrors}
              trigger={nodeData.nodeClass}
              selectedResourceName={startUtils.getResourceValue(nodeData)}
              propertyMapDisallowedIds={[CAMPAIGN_LABEL]}
              isReadOnly={isTileEditorReadOnly}
            />
          )}
          {/* show Entrance Limits only if the user has DraftMessaging permission */}
          {canDraftMessaging && (
            <EntranceLimitForm
              simultaneousLimit={workflowData.workflowSimultaneousLimit}
              onSimultaneousLimitUpdate={(limit) =>
                updateInternalWorkflowData({ workflowSimultaneousLimit: limit })
              }
              lifetimeLimit={workflowData.workflowLifetimeLimit}
              onLifetimeLimitUpdate={(limit) =>
                updateInternalWorkflowData({ workflowLifetimeLimit: limit })
              }
              lifetimeLimitError={errors.lifetimeLimit}
              simultaneousLimitError={errors.simultaneousLimit}
              isReadOnly={isTileEditorReadOnly}
            />
          )}
        </SimpleOverlayContainer>
      </FormSectionContent>
    </NodeEditor>
  );
};

export const StartNodeEditor = (props) => {
  const isStartNodeEditorRefreshEnabled = useSelector(getIsStartTileRefreshEnabled);

  return isStartNodeEditorRefreshEnabled ? (
    <StartNodeEditorRefresh {...props} />
  ) : (
    <StartNodeEditorCurrent {...props} />
  );
};
